import { uniqBy } from "lodash";

export interface TagWithParents {
  __typename?: "FileTag";
  label: string;
  parentTags?: TagWithParents[] | null;
}

export interface TreeNode {
  data: TagWithParents;
  children: TreeNode[];
}

interface FileWithTags {
  labels: TagWithParents[];
}

function buildTreeNode(tag: TagWithParents, allTags: TagWithParents[], visited = new Set<string>()): TreeNode {
  // Avoid infinite loops by checking if we've already processed this tag
  if (visited.has(tag.label)) {
    return {
      data: tag,
      children: [],
    };
  }

  // Mark this tag as visited
  const newVisited = new Set(visited);
  newVisited.add(tag.label);

  // Find children: labels that have this label in their parentTags
  const children = allTags
    .filter((t) => t.parentTags?.some((parent) => parent.label === tag.label))
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    data: tag,
    children: children.map((child) => buildTreeNode(child, allTags, newVisited)),
  };
}

/**
 * Recursively collects all parent tags from a given tag, including nested parents
 */
function collectAllParentTags(tag: TagWithParents, collected = new Set<string>()): TagWithParents[] {
  if (!tag.parentTags || tag.parentTags.length === 0) {
    return [];
  }

  const result: TagWithParents[] = [];

  for (const parent of tag.parentTags) {
    // Avoid infinite loops by checking if we've already processed this tag
    if (!collected.has(parent.label)) {
      collected.add(parent.label);
      // Recursively collect this parent and all of its parents
      result.push(parent, ...collectAllParentTags(parent, collected));
    }
  }

  return result;
}

export function buildTagTree(files: FileWithTags[]): TreeNode[] {
  // Collect all full tags first (with parentTags property)
  const fullTags = files.flatMap((file) => file.labels);

  // Recursively collect all nested parent tag references
  const allParentRefs: TagWithParents[] = [];

  for (const tag of fullTags) {
    allParentRefs.push(...collectAllParentTags(tag));
  }

  // Deduplicate by label, prioritizing full tags over parent references
  const allTags = uniqBy([...fullTags, ...allParentRefs], (tag) => tag?.label);

  // Find root labels (no parents or empty parents)
  const roots = allTags
    .filter((tag) => !tag.parentTags || tag.parentTags.length === 0)
    .sort((a, b) => a.label.localeCompare(b.label));

  return roots.map((root) => buildTreeNode(root, allTags));
}

export function allAncestorLabels(parentTags: TagWithParents[] | null | undefined): string[] {
  if (!parentTags || parentTags.length === 0) {
    return [];
  }

  return parentTags.flatMap((parent) => [parent.label, ...allAncestorLabels(parent.parentTags)]);
}
